/**
 * SocialFollowCTA — three-platform "Follow us" call-to-action.
 *
 * Reused on both the public /menu page (phone/web) and the customer-facing
 * /menu-board TV display. Renders a tile per configured platform with the
 * handle, follower count (if set), and a tap-target that opens the
 * platform's profile in a new tab.
 *
 * Variants:
 *   • `variant="standard"` — for the /menu page. Card layout, full chrome,
 *      with a tappable QR code so customers can scan and follow without
 *      typing the handle.
 *   • `variant="board"`    — for the TV. Slim, high-contrast, at-a-glance.
 *
 * Auto-hides if the social config is missing or the section is disabled.
 *
 * Follower counts are formatted with `formatCount` so 5234 → "5.2K" so
 * the card stays compact even with large audiences.
 */
import { Instagram, Facebook, Sparkles } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

// TikTok isn't in lucide-react. Inline SVG so we don't add a dep.
function TikTokIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.85a8.16 8.16 0 0 0 4.77 1.52V6.92a4.85 4.85 0 0 1-1.84-.23z" />
    </svg>
  );
}

function formatCount(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return null;
  const num = Number(n);
  if (num < 1000) return String(num);
  if (num < 10_000) return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  if (num < 1_000_000) return Math.round(num / 1000) + "K";
  return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
}

function platformUrl(p) {
  // Prefer explicit URL, otherwise build one from the handle.
  if (p.url) return p.url;
  if (!p.handle) return null;
  const h = p.handle.replace(/^@/, "");
  if (p.network === "instagram") return `https://instagram.com/${h}`;
  if (p.network === "tiktok") return `https://tiktok.com/@${h}`;
  if (p.network === "facebook") return `https://facebook.com/${h}`;
  return null;
}

const NETWORKS = [
  {
    network: "instagram",
    label: "Instagram",
    Icon: Instagram,
    // Instagram's signature pink-orange gradient
    gradient: "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
    accent: "from-pink-500 to-orange-400",
  },
  {
    network: "tiktok",
    label: "TikTok",
    Icon: TikTokIcon,
    // TikTok cyan-magenta-on-black
    gradient: "linear-gradient(135deg, #25F4EE 0%, #000 50%, #FE2C55 100%)",
    accent: "from-cyan-400 to-pink-500",
  },
  {
    network: "facebook",
    label: "Facebook",
    Icon: Facebook,
    gradient: "linear-gradient(135deg, #1877F2 0%, #0d47a1 100%)",
    accent: "from-blue-500 to-blue-700",
  },
];

function buildEntries(social) {
  if (!social || social.enabled === false) return [];
  const out = [];
  for (const cfg of NETWORKS) {
    const handle = social[`${cfg.network}_handle`];
    const url = social[`${cfg.network}_url`];
    const followers = social[`${cfg.network}_followers`];
    if (!handle && !url) continue;
    out.push({
      ...cfg,
      handle: handle || "",
      url: url || "",
      followers,
    });
  }
  return out;
}

/* ---------- Standard (web /menu) variant ---------- */
function StandardTile({ entry }) {
  const href = platformUrl(entry);
  const count = formatCount(entry.followers);
  return (
    <a
      href={href || "#"}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`social-tile-${entry.network}`}
      className="group relative overflow-hidden rounded-2xl bg-card border-2 border-transparent hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 p-5 flex items-center gap-4"
      style={{ boxShadow: "0 1px 0 rgba(0,0,0,0.04)" }}
    >
      {/* Coloured stripe on the left so each platform is identifiable */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1.5"
        style={{ background: entry.gradient }}
      />
      <span
        className="grid place-items-center w-12 h-12 rounded-2xl text-white shrink-0 shadow-sm"
        style={{ background: entry.gradient }}
      >
        <entry.Icon className="w-6 h-6" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-display font-bold text-lg leading-tight">
          {entry.label}
        </p>
        <p className="text-sm text-muted-foreground truncate">
          {entry.handle ? `@${entry.handle}` : "Follow us"}
        </p>
      </div>
      {count && (
        <div className="text-right shrink-0">
          <p
            className="font-display font-extrabold text-2xl tabular-nums leading-none"
            data-testid={`social-count-${entry.network}`}
          >
            {count}
          </p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-1">
            followers
          </p>
        </div>
      )}
    </a>
  );
}

export function SocialFollowCTA({ social }) {
  const entries = buildEntries(social);
  if (!entries.length) return null;
  return (
    <section
      data-testid="social-cta"
      className="relative rounded-3xl p-6 sm:p-8 my-12 overflow-hidden"
      style={{
        background:
          "radial-gradient(900px circle at 0% 0%, hsla(330 95% 92% / 0.7) 0%, transparent 55%)," +
          "radial-gradient(900px circle at 100% 100%, hsla(252 90% 92% / 0.7) 0%, transparent 55%)," +
          "linear-gradient(180deg, hsl(330 60% 99%) 0%, hsl(252 50% 99%) 100%)",
      }}
    >
      <div className="text-center mb-6">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/80 border border-primary/15 text-[10px] font-bold text-primary uppercase tracking-[0.2em] shadow-sm">
          <Sparkles className="w-3 h-3 text-accent" />
          Stay in the loop
        </span>
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl mt-3 leading-tight">
          <span className="bg-gradient-to-r from-violet-600 via-pink-500 to-amber-400 bg-clip-text text-transparent">
            Follow the boba
          </span>
        </h2>
        {social.tagline && (
          <p
            className="text-base text-muted-foreground mt-2 max-w-md mx-auto"
            data-testid="social-cta-tagline"
          >
            {social.tagline}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
        {entries.map((e) => (
          <StandardTile key={e.network} entry={e} />
        ))}
      </div>
    </section>
  );
}

/* ---------- Board (TV /menu-board) variant ---------- */
/**
 * QR-forward strip designed for the customer-facing TV menu board.
 * Each platform gets a sizeable scannable QR alongside its handle so
 * customers can pull out their phone, scan from across the room, and
 * follow on the spot. Sized to be readable from ~6-8 feet away.
 */
export function SocialFollowBar({ social, vertical = false }) {
  const entries = buildEntries(social);
  if (!entries.length) return null;
  return (
    <section
      data-testid="menu-board-social"
      className="relative rounded-2xl px-5 py-4 overflow-hidden border border-white/70 bg-white/85 backdrop-blur-sm shadow-sm"
    >
      <div className={vertical ? "mb-2.5" : "flex items-center gap-4 mb-3"}>
        <p className="font-display text-[10px] uppercase tracking-[0.32em] text-muted-foreground font-bold">
          Follow us
        </p>
        <p
          className={
            vertical
              ? "font-display text-sm font-extrabold leading-tight text-foreground mt-0.5"
              : "font-display text-base lg:text-lg font-extrabold leading-tight text-foreground flex-1 min-w-0 truncate"
          }
        >
          {social.tagline || "Get boba drops in your feed"}
        </p>
      </div>
      <div
        className={vertical ? "flex flex-col gap-2.5" : "grid gap-3"}
        style={vertical ? undefined : { gridTemplateColumns: `repeat(${entries.length}, minmax(0, 1fr))` }}
      >
        {entries.map((e) => {
          const count = formatCount(e.followers);
          const href = platformUrl(e);
          return (
            <div
              key={e.network}
              data-testid={`menu-board-social-${e.network}`}
              className="flex items-center gap-3 rounded-xl bg-white p-2.5 border border-border/40"
            >
              {/* Big-enough QR for ~6-8 ft TV viewing. Slightly smaller in the
                  sidebar layout so the stacked tiles stay compact. */}
              {href && (
                <div
                  className="rounded-lg bg-white p-1 shrink-0 border border-border/50"
                  data-testid={`menu-board-social-qr-${e.network}`}
                  title={`Scan to follow on ${e.label}`}
                >
                  <QRCodeSVG
                    value={href}
                    size={vertical ? 70 : 88}
                    level="M"
                    fgColor="#1a1a1a"
                    bgColor="#ffffff"
                  />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-white shadow-sm"
                  style={{ background: e.gradient }}
                >
                  <e.Icon className="w-3.5 h-3.5" />
                  <span className="font-display font-bold text-[11px] uppercase tracking-widest">
                    {e.label}
                  </span>
                </span>
                <p className="font-display font-extrabold text-base lg:text-lg leading-tight mt-1 truncate">
                  {e.handle ? `@${e.handle}` : "Follow us"}
                </p>
                {count && (
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-0.5">
                    <span className="font-display font-extrabold text-sm text-foreground tabular-nums normal-case tracking-normal mr-1">
                      {count}
                    </span>
                    followers
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
